// Wave 3.99ae — Multi-select recipient picker for internal mail compose.
// Wave 4.x — Also surfaces the user's PERSONAL groups: selecting a group
// merges all its members into the "To" bar in one click (no duplicates).
import React, { useEffect, useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { Check, ChevronsUpDown, X, Users, UsersRound } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

const ROLE_LABEL = { super_admin: 'Super Admin', company_admin: 'Company Admin', sales_person: 'Sales', sales_manager: 'Sales Mgr', user: 'User' };

export const RecipientPicker = ({ selected, onChange, showGroups = true }) => {
  const { effectiveCompanyId } = useAuth();
  const [open, setOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoaded(false);
      const params = effectiveCompanyId ? { company_id: effectiveCompanyId } : {};
      try {
        // Wave 4.02 — Scope recipients to the ACTIVE company (matters for
        // Super Admin, whose backend candidate list is now company-scoped).
        const reqs = [api.get('/mail/recipients', { params })];
        if (showGroups) reqs.push(api.get('/mail/groups', { params }));
        const [rc, gr] = await Promise.all(reqs);
        if (!alive) return;
        setCandidates(Array.isArray(rc.data) ? rc.data : []);
        if (showGroups) setGroups(Array.isArray(gr?.data) ? gr.data : []);
      } catch { /* noop */ }
      finally { if (alive) setLoaded(true); }
    })();
    return () => { alive = false; };
  }, [effectiveCompanyId, showGroups]);

  const selectedIds = useMemo(() => new Set(selected.map((s) => s.id)), [selected]);
  const toggle = (c) => {
    if (selectedIds.has(c.id)) onChange(selected.filter((s) => s.id !== c.id));
    else onChange([...selected, c]);
  };
  const remove = (id) => onChange(selected.filter((s) => s.id !== id));

  // Merge every member of a group into the current selection, skipping any
  // recipient that is already picked (no duplicates).
  const addGroup = (g) => {
    const have = new Set(selected.map((s) => s.id));
    const additions = (g.members || [])
      .filter((m) => m.id && !have.has(m.id))
      .map((m) => ({ id: m.id, name: m.name, email: m.email, role: m.role }));
    if (additions.length === 0) return;
    onChange([...selected, ...additions]);
  };

  return (
    <div data-testid="mail-recipient-picker">
      <div className="flex flex-wrap items-center gap-1.5 min-h-[38px] rounded-md border border-[var(--divider)] bg-white px-2 py-1.5">
        {selected.length === 0 && <span className="text-sm text-slate-400 px-1">Select recipients…</span>}
        {selected.map((s) => (
          <Badge key={s.id} variant="secondary" className="gap-1 border border-[var(--diamond-border)] bg-[var(--diamond-bg)] text-[var(--diamond-icon)]" data-testid={`mail-recipient-chip-${s.id}`}>
            {s.name || s.email}
            <button type="button" onClick={() => remove(s.id)} className="ml-0.5 hover:text-rose-600" aria-label={`Remove ${s.name || s.email}`}>
              <X size={12} />
            </button>
          </Badge>
        ))}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-slate-500 hover:bg-slate-100"
              data-testid="mail-recipient-add-button"
            >
              <Users size={13} /> Add <ChevronsUpDown size={12} />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[340px] p-0" align="end">
            <Command>
              <CommandInput placeholder="Search groups & teammates…" data-testid="mail-recipient-search" />
              <CommandList>
                <CommandEmpty>{loaded ? 'No matches found.' : 'Loading…'}</CommandEmpty>
                {showGroups && groups.length > 0 && (
                  <>
                    <CommandGroup heading="Your groups">
                      {groups.map((g) => (
                        <CommandItem
                          key={g.id}
                          value={`group ${g.name}`}
                          onSelect={() => { addGroup(g); }}
                          data-testid={`mail-recipient-group-${g.id}`}
                        >
                          <UsersRound size={14} className="mr-2 text-[var(--diamond-icon)]" />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm text-[var(--ink-navy)] truncate">{g.name}</div>
                            <div className="text-[11px] text-slate-400 truncate">{g.member_count} member{g.member_count === 1 ? '' : 's'} · adds everyone</div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    <CommandSeparator />
                  </>
                )}
                <CommandGroup heading={showGroups && groups.length > 0 ? 'People' : undefined}>
                  {candidates.map((c) => (
                    <CommandItem key={c.id} value={`${c.name} ${c.email}`} onSelect={() => toggle(c)} data-testid={`mail-recipient-option-${c.id}`}>
                      <Check size={14} className={`mr-2 ${selectedIds.has(c.id) ? 'opacity-100 text-[var(--diamond-icon)]' : 'opacity-0'}`} />
                      <div className="min-w-0">
                        <div className="text-sm text-[var(--ink-navy)] truncate">{c.name || c.email}</div>
                        <div className="text-[11px] text-slate-400 truncate">{c.email} · {ROLE_LABEL[c.role] || c.role}</div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default RecipientPicker;
